<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Fecha</label>
              <b-form-input v-model="filters.date" type="date" size="sm" />
            </div>
            <div class="list-view-filter" style="width: 130px;">
              <label class="filter-label">Desde</label>
              <b-form-input v-model="filters.time_start" type="time" size="sm" />
            </div>
            <div class="list-view-filter" style="width: 130px;">
              <label class="filter-label">Hasta</label>
              <b-form-input v-model="filters.time_end" type="time" size="sm" />
            </div>
            <div v-if="!isSeller" class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Vendedor</label>
              <v-select :options="arr.select.sellers" v-model="filters.sellers" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 240px;">
              <label class="filter-label">Mapa</label>
              <b-form-select :options="maps.styles" v-model="maps.style" size="sm" @change="updateMapStyle" />
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterLocations" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card no-body class="sellers-locations-map-wrapper">
        <div class="sellers-locations-map" v-if="maps.markerLatLng.length">
          <l-map
            :options="maps.options"
            :zoom="maps.zoom"
            :center="maps.center"
            :bounds="maps.bounds"
            :bounds-options="maps.boundsOptions"
          >
            <l-tile-layer :url="maps.url" :attribution="maps.attribution"></l-tile-layer>
            <l-polyline v-for="route in maps.routes"
                        :key="route.seller_id"
                        :lat-lngs="route.points"
                        :color="route.color"
                        :weight="4"
                        :opacity="0.7"></l-polyline>
            <l-circle-marker v-for="(item, key) in maps.markerLatLng"
                      v-bind:key="key"
                      :lat-lng="item.geo"
                      :radius="item.item.order_id ? 7 : 5"
                      :color="item.color"
                      :fill-color="item.color"
                      :fill-opacity="0.85"
                      :weight="1">
              <l-popup>
                <div>
                  <div><b>Fecha:</b> {{moment(item.item.captured_at).format('DD/MM/YYYY HH:mm')}}</div>
                  <div v-if="item.item.seller && item.item.seller.staff"><b>Vendedor:</b> {{item.item.seller.staff.name}}</div>
                  <div v-if="item.item.order_id"><b>Pedido:</b> #{{item.item.order_id}}</div>
                  <div v-if="item.dist_prev_km != null"><b>Tramo:</b> {{item.dist_prev_km}} km</div>
                  <div v-if="item.dist_total_km != null"><b>Acumulado:</b> {{item.dist_total_km}} km</div>
                  <div v-if="item.item.observation"><b>Obs:</b> {{item.item.observation}}</div>
                </div>
              </l-popup>
            </l-circle-marker>
            <l-marker v-for="(item, key) in maps.startEndMarkers"
                      v-bind:key="'se-' + key"
                      :lat-lng="item.geo"
                      :icon="item.icon"
                      :z-index-offset="item.zIndex">
              <l-popup>
                <div>
                  <div><b>{{item.label}}:</b> {{moment(item.item.captured_at).format('DD/MM/YYYY HH:mm')}}</div>
                  <div v-if="item.item.seller && item.item.seller.staff"><b>Vendedor:</b> {{item.item.seller.staff.name}}</div>
                  <div v-if="item.item.order_id"><b>Pedido:</b> #{{item.item.order_id}}</div>
                  <div v-if="item.dist_prev_km != null"><b>Tramo:</b> {{item.dist_prev_km}} km</div>
                  <div v-if="item.dist_total_km != null"><b>Acumulado:</b> {{item.dist_total_km}} km</div>
                  <div v-if="item.item.observation"><b>Obs:</b> {{item.item.observation}}</div>
                </div>
              </l-popup>
            </l-marker>
          </l-map>
        </div>
        <EmptyState v-else-if="!hasFiltered" icon="location_on" title="Consultá los recorridos" description="Seleccioná una fecha y aplicá los filtros para ver el recorrido." />
        <EmptyState v-else icon="location_off" title="No se encontraron ubicaciones" description="Ajustá los filtros para ampliar la búsqueda." />
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import moment from 'moment'

  import { LMap, LTileLayer, LMarker, LCircleMarker, LPolyline, LPopup } from 'vue2-leaflet'
  import L from 'leaflet'
  import 'leaflet/dist/leaflet.css';
  import markerIcon from 'leaflet/dist/images/marker-icon.png'
  import markerShadow from 'leaflet/dist/images/marker-shadow.png'

  const MAP_STYLE_KEY = window.location.origin + '_SellersLocationsMapStyle'

  delete L.Icon.Default.prototype._getIconUrl

  L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 25 * 1.15],
    iconAnchor: [25 / 2, 25 * 1.15],
    shadowSize: [25, 25 * 1.15],
    shadowAnchor: [25 / 2, 25 * 1.15],
  })

  export default {
    components: {
      LMap,
      LTileLayer,
      LMarker,
      LCircleMarker,
      LPolyline,
      LPopup,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.VENDEDORES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'listLocationsSellers',
          elements: {}
        },
        filters: {
          date: moment().format('YYYY-MM-DD'),
          time_start: '',
          time_end: '',
          sellers: null,
        },
        filtersOpen: false,
        hasFiltered: false,
        arr: {
          select: {
            sellers: [],
          },
          locations: [],
        },
        maps: {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
          attribution: '&copy; OpenStreetMap contributors &copy; Esri',
          styles: [
            { value: 'esri_street', text: 'Esri Street' },
            { value: 'esri_imagery', text: 'Esri Satelite' },
            { value: 'carto_voyager', text: 'Carto Voyager' },
            { value: 'carto_light', text: 'Carto Light' },
            { value: 'carto_dark', text: 'Carto Dark' },
            { value: 'osm', text: 'OpenStreetMap' },
            { value: 'osm_hot', text: 'OSM HOT' },
            { value: 'opentopo', text: 'OpenTopo' },
          ],
          style: 'osm',
          options: {zoomControl: false, zoomSnap: true},
          zoom: 15,
          center: [],
          bounds: null,
          boundsOptions: { padding: [20, 20] },
          markerLatLng: [],
          startEndMarkers: [],
          routes: [],
          colors: [
            '#0B6E99',
            '#C44536',
            '#2D936C',
            '#6C4AB6',
            '#F4A261',
            '#264653',
            '#E76F51',
            '#1D3557',
            '#7F5539',
            '#5FAD56'
          ],
        },
      }
    },
    created () {
      Helper.hasAccessView(this.access)
    },
    mounted () {
      this.maps.style = this.getMapStyleFromStorage()
      this.updateMapStyle()
      this.filtersOpen = StorageLocal.getValue('filters_open_list_locations_sellers') === 'true'
      this.setup()
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_list_locations_sellers', value ? 'true' : 'false')
      },
    },
    computed: {
      isSeller() {
        return Helper.getSeller() ? true : false
      },
    },
    methods: {
      setup() {
        if(this.isSeller) {
          this.filters.sellers = {
            code: Helper.getSeller().id,
            label: Helper.getSeller().name,
          }
        } else if(this.$route.query.sellers_id) {
          // Acceso directo desde "Ver recorrido" en el listado de
          // vendedores -- la fecha ya default a hoy (filters.date), así
          // que alcanza con preseleccionar el vendedor.
          this.filters.sellers = {
            code: Number(this.$route.query.sellers_id),
            label: this.$route.query.sellers_name || '',
          }
          this.loadSellers()
        } else {
          this.loadSellers()
        }
        this.filterLocations()
      },
      loadSellers() {
        var result = serviceAPI.obtenerVendedor()
        result.then((response) => {
          var data = response.data
          this.arr.select.sellers = []
          data.forEach(element => {
            this.arr.select.sellers.push({ code: element.id, label: element.name })
          })
        })
      },
      filterLocations() {
        if (!this.filters.date) {
          this.filters.date = moment().format('YYYY-MM-DD')
        }
        let loader = this.$loading.show()
        this.hasFiltered = true

        var result = serviceAPI.filtrarUbicaciones(this.filters)
        result.then((response) => {
          loader.hide()
          var data = response.data || []
          this.arr.locations = data
          this.buildMap(data)
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      buildMap(data) {
        this.maps.markerLatLng = []
        this.maps.routes = []
        this.maps.center = []
        this.maps.bounds = null
        this.maps.startEndMarkers = []

        if(!data.length) {
          return
        }

        var sumLat = 0
        var sumLng = 0
        var count = 0
        var routesBySeller = {}
        var allPoints = []
        var firstBySeller = {}
        var lastBySeller = {}
        var lastPointBySeller = {}
        var totalBySeller = {}

        data.forEach((item) => {
          if(item.lat && item.lng) {
            var lat = parseFloat(item.lat)
            var lng = parseFloat(item.lng)
            if(!isNaN(lat) && !isNaN(lng)) {
              var color = this.getColorBySellerId(item.sellers_id)
              var icon = this.getMarkerIcon(color, item.order_id, item.source)
              var distPrev = null
              var distTotal = null

              if(lastPointBySeller[item.sellers_id]) {
                distPrev = this.calcDistanceKm(lastPointBySeller[item.sellers_id].lat, lastPointBySeller[item.sellers_id].lng, lat, lng)
                totalBySeller[item.sellers_id] = (totalBySeller[item.sellers_id] || 0) + distPrev
                distTotal = totalBySeller[item.sellers_id]
              } else {
                totalBySeller[item.sellers_id] = 0
                distPrev = 0
                distTotal = 0
              }

              this.maps.markerLatLng.push({
                geo: [lat, lng],
                item: item,
                color: color,
                icon: icon,
                zIndex: 0,
                dist_prev_km: distPrev !== null ? distPrev.toFixed(2) : null,
                dist_total_km: distTotal !== null ? distTotal.toFixed(2) : null,
              })

              if(!routesBySeller[item.sellers_id]) {
                routesBySeller[item.sellers_id] = {
                  seller_id: item.sellers_id,
                  color: color,
                  points: [],
                }
              }
              routesBySeller[item.sellers_id].points.push([lat, lng])

              if(!firstBySeller[item.sellers_id]) {
                firstBySeller[item.sellers_id] = { item: item, geo: [lat, lng], color: color }
              }
              lastBySeller[item.sellers_id] = { item: item, geo: [lat, lng], color: color }
              lastPointBySeller[item.sellers_id] = { lat: lat, lng: lng }

              allPoints.push([lat, lng])
              sumLat += lat
              sumLng += lng
              count += 1
            }
          }
        })

        this.maps.routes = Object.values(routesBySeller)
        this.maps.startEndMarkers = []
        Object.keys(firstBySeller).forEach((sellerId) => {
          var start = firstBySeller[sellerId]
          var end = lastBySeller[sellerId]

          if(start) {
            this.maps.startEndMarkers.push({
              geo: start.geo,
              item: start.item,
              label: 'Inicio',
              icon: this.getStartEndIcon(start.color, 'start'),
              zIndex: 1000,
              dist_prev_km: '0.00',
              dist_total_km: '0.00',
            })
          }
          if(end && (!start || end.item.id !== start.item.id)) {
            var endTotal = totalBySeller[sellerId] != null ? totalBySeller[sellerId].toFixed(2) : null
            this.maps.startEndMarkers.push({
              geo: end.geo,
              item: end.item,
              label: 'Fin',
              icon: this.getStartEndIcon(end.color, 'end'),
              zIndex: 1000,
              dist_prev_km: null,
              dist_total_km: endTotal,
            })
          }
        })

        if(count === 1) {
          this.maps.zoom = 16
          this.maps.center = [sumLat / count, sumLng / count]
        } else if(count > 1) {
          this.maps.zoom = 15
          this.maps.center = [sumLat / count, sumLng / count]
          this.maps.bounds = allPoints
        } else if(count > 0) {
          this.maps.center = [sumLat / count, sumLng / count]
        }
      },
      getColorBySellerId(sellerId) {
        var id = parseInt(sellerId, 10)
        if(isNaN(id)) {
          return '#5a5a5a'
        }
        var index = Math.abs(id) % this.maps.colors.length
        return this.maps.colors[index]
      },
      getMarkerIcon(color, orderId, source) {
        var isOrder = orderId ? true : false
        if(!isOrder && source) {
          isOrder = source === 'order'
        }
        var size = isOrder ? 18 : 12
        var className = isOrder ? 'seller-marker seller-marker--order' : 'seller-marker'
        var label = isOrder ? '<span class="seller-marker-label">P</span>' : ''
        var html = '<div class="' + className + '" style="background:' + color + '">' + label + '</div>'
        return L.divIcon({
          html: html,
          className: 'seller-marker-container',
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        })
      },
      calcDistanceKm(lat1, lng1, lat2, lng2) {
        var toRad = function (value) {
          return value * Math.PI / 180
        }

        var R = 6371
        var dLat = toRad(lat2 - lat1)
        var dLng = toRad(lng2 - lng1)
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2)
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        return R * c
      },
      getStartEndIcon(color, type) {
        var label = type === 'end' ? 'F' : 'I'
        var html = '<div class="seller-marker-startend" style="border-color:' + color + '; color:' + color + '">' + label + '</div>'
        return L.divIcon({
          html: html,
          className: 'seller-marker-container',
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        })
      },
      getMapStyleFromStorage() {
        var value = localStorage.getItem(MAP_STYLE_KEY)
        return value ? value : this.maps.style
      },
      setMapStyleToStorage() {
        localStorage.setItem(MAP_STYLE_KEY, this.maps.style)
      },
      updateMapStyle() {
        if(this.maps.style === 'esri_street') {
          this.maps.url = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'
          this.maps.attribution = '&copy; OpenStreetMap contributors &copy; Esri'
        } else if(this.maps.style === 'esri_imagery') {
          this.maps.url = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          this.maps.attribution = '&copy; OpenStreetMap contributors &copy; Esri'
        } else if(this.maps.style === 'carto_voyager') {
          this.maps.url = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
          this.maps.attribution = '&copy; OpenStreetMap contributors &copy; CARTO'
        } else if(this.maps.style === 'carto_dark') {
          this.maps.url = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
          this.maps.attribution = '&copy; OpenStreetMap contributors &copy; CARTO'
        } else if(this.maps.style === 'osm') {
          this.maps.url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          this.maps.attribution = '&copy; OpenStreetMap contributors'
        } else if(this.maps.style === 'osm_hot') {
          this.maps.url = 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png'
          this.maps.attribution = '&copy; OpenStreetMap contributors, HOT'
        } else if(this.maps.style === 'opentopo') {
          this.maps.url = 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'
          this.maps.attribution = '&copy; OpenStreetMap contributors, OpenTopoMap'
        } else {
          this.maps.url = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'
          this.maps.attribution = '&copy; OpenStreetMap contributors &copy; Esri'
        }

        this.setMapStyleToStorage()
      },
    }
  }
</script>

<style scoped>
  .sellers-locations-map {
    padding: 0px;
    height: calc(100vh - 160px);
    min-height: 600px;
  }
  .sellers-locations-map .leaflet-container {
    height: 100%;
    width: 100%;
  }
</style>

<style>
  .seller-marker-container {
    background: transparent;
    border: none;
  }
  .seller-marker {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.15);
  }
  .seller-marker--order {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    transform: rotate(45deg);
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.2);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .seller-marker-label {
    transform: rotate(-45deg);
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  .seller-marker-startend {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid #111;
    background: #fff;
    font-weight: 700;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.15);
  }
</style>
